import { useMutation } from '@tanstack/react-query';
import { Play } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Card, CardContent, CardHeader } from '@/components/ui';
import { Textarea } from '@/components/ui/Textarea';
import { inferenceApi, type InferenceSandboxResult } from '@/lib/api';

const SAMPLE_CONSTRUCT = `PREFIX ett: <https://energytransition.kg/ontology#>
PREFIX rdfs: <http://www.w3.org/2000/01/rdf-schema#>

CONSTRUCT { ?org ett:hasTechnologyExposure ?tech }
WHERE {
  ?org a ett:Organization ; ett:investsIn ?proj .
  ?proj ett:usesTechnology ?tech .
}`;

export function InferenceSandbox() {
  const { t } = useTranslation(['studio', 'common']);
  const [query, setQuery] = useState(SAMPLE_CONSTRUCT);
  const [result, setResult] = useState<InferenceSandboxResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: (q: string) => inferenceApi.previewSandbox(q),
    onSuccess: (r) => {
      setResult(r);
      setError(null);
    },
    onError: (e: Error) => {
      setError(e.message);
      setResult(null);
    },
  });

  return (
    <div className="space-y-4">
      <header>
        <h2 className="text-h2 text-brand-900">{t('studio:inference.sandbox.title')}</h2>
        <p className="mt-1 text-small text-slate-600">
          {t('studio:inference.sandbox.subtitle')}
        </p>
      </header>

      <Card>
        <CardHeader>
          <label className="text-h4 text-slate-700" htmlFor="sandbox-construct">
            {t('studio:inference.sandbox.queryLabel')}
          </label>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            id="sandbox-construct"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            rows={10}
            className="font-mono text-small"
          />
          <div className="flex justify-end">
            <Button
              onClick={() => run.mutate(query)}
              disabled={run.isPending || query.trim().length < 10}
            >
              <Play className="mr-1 h-3.5 w-3.5" aria-hidden />
              {run.isPending ? t('studio:inference.sandbox.running') : t('studio:inference.sandbox.run')}
            </Button>
          </div>
          {error && (
            <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-small text-red-700">
              {error}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <h3 className="text-h3 text-brand-900">{t('studio:inference.sandbox.result')}</h3>
        </CardHeader>
        <CardContent>
          {!result && !run.isPending && (
            <p className="text-small text-slate-500">{t('studio:inference.sandbox.empty')}</p>
          )}
          {result && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <Metric
                  label={t('studio:inference.sandbox.metrics.triples')}
                  value={result.triple_count}
                />
                <Metric
                  label={t('studio:inference.sandbox.metrics.subjects')}
                  value={result.distinct_subjects}
                />
                <Metric
                  label={t('studio:inference.sandbox.metrics.predicates')}
                  value={result.distinct_predicates}
                />
              </div>
              {result.inferred_triples.length === 0 ? (
                <p className="text-small text-slate-500">
                  {t('studio:inference.sandbox.noTriples')}
                </p>
              ) : (
                <div>
                  <h4 className="mb-2 text-h4 text-slate-700">
                    {t('studio:inference.sandbox.sample')}
                  </h4>
                  <ul className="space-y-1 font-mono text-small text-slate-700">
                    {result.inferred_triples.map((tri, i) => (
                      <li key={i} className="rounded bg-slate-50 px-2 py-1">
                        <span className="text-brand-900">{tri.subject}</span>{' '}
                        <span className="text-accent-700">{tri.predicate}</span>{' '}
                        <span>{tri.object}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface MetricProps {
  label: string;
  value: number;
}

function Metric({ label, value }: MetricProps) {
  return (
    <div className="rounded border border-slate-200 p-3">
      <p className="text-small text-slate-500">{label}</p>
      <p className="mt-1 text-h2 font-semibold text-brand-900">{value.toLocaleString()}</p>
    </div>
  );
}
